import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { progressService, canvasService } from '../api/services';
import useApi from '../hooks/useApi';
import DashboardLayout from '../components/layout/DashboardLayout';
import {
  CheckCircle2, Lock, Video, PenLine, AlertCircle,
  TrendingUp, ArrowRight, ChevronDown, ChevronUp,
  Play, FileText, Link2, Loader2, Circle, BookOpen,
  Calendar, Eye,
} from 'lucide-react';
import toast from 'react-hot-toast';

// ── Content type icon ─────────────────────────────────────────
const ContentIcon = ({ type }) => {
  if (type === 'VIDEO')    return <Play size={12} className="text-forest-500" />;
  if (type === 'DOCUMENT') return <FileText size={12} className="text-forest-500" />;
  return <Link2 size={12} className="text-forest-500" />;
};

// ── Single content item ───────────────────────────────────────
const ContentRow = ({ content, onComplete, locked }) => {
  const [completing, setCompleting] = useState(false);
  const done = content.progress?.status === 'COMPLETED';

  const handleComplete = async () => {
    if (done || locked) return;
    setCompleting(true);
    try {
      await progressService.markComplete(content.id);
      toast.success('Marked as complete!');
      onComplete();
    } catch {
      toast.error('Could not mark complete');
    } finally {
      setCompleting(false);
    }
  };

  return (
    <div className={`flex items-center gap-3 px-4 py-2.5 rounded-xl transition-colors ${
      done ? 'bg-forest-50/50' : locked ? 'bg-cream-50 opacity-50' : 'bg-cream-50'
    }`}>
      <ContentIcon type={content.contentType} />
      <span className={`flex-1 text-sm truncate ${done ? 'text-forest-400 line-through decoration-forest-300' : 'text-forest-700'}`}>
        {content.title}
      </span>
      {content.duration && (
        <span className="text-xs text-forest-400 flex-shrink-0">{Math.round(content.duration / 60)}m</span>
      )}
      {done ? (
        <CheckCircle2 size={15} className="text-forest-500 flex-shrink-0" />
      ) : locked ? (
        <Lock size={13} className="text-cream-400 flex-shrink-0" />
      ) : (
        <button onClick={handleComplete} disabled={completing}
          className="text-xs text-forest-600 border border-forest-200 hover:bg-forest-50 px-2.5 py-1 rounded-full transition-colors flex-shrink-0 flex items-center gap-1">
          {completing ? <Loader2 size={10} className="animate-spin" /> : <Circle size={10} />}
          Done
        </button>
      )}
    </div>
  );
};

// ── Canvas submission status badge ────────────────────────────
const CanvasBadge = ({ submission }) => {
  if (!submission) return null;
  const styles = {
    SUBMITTED:          'bg-amber-50 text-amber-700 border-amber-200',
    GRADED:             'bg-forest-50 text-forest-700 border-forest-200',
    REVISION_REQUESTED: 'bg-orange-50 text-orange-700 border-orange-200',
  };
  return (
    <span className={`text-[10px] font-semibold border px-2 py-0.5 rounded-full ${styles[submission.status] ?? ''}`}>
      {submission.status === 'GRADED'
        ? `✓ Passed (${submission.score})`
        : submission.status}
    </span>
  );
};

// ── Stage block ───────────────────────────────────────────────
const StageBlock = ({ stage, isLevelLocked, onComplete }) => {
  const totalContent     = stage.contents?.length ?? 0;
  const completedContent = stage.contents?.filter(c => c.progress?.status === 'COMPLETED').length ?? 0;
  const contentAllDone   = totalContent > 0 && completedContent === totalContent;
  const hasNoContent     = totalContent === 0;

  // Canvas unlocks when all content done (or no content)
  const canvasUnlocked   = !isLevelLocked && (hasNoContent || contentAllDone);
  const canvas           = stage.canvasActivity;
  const submission       = canvas?.submission ?? null;
  const canvasPassed     = submission?.status === 'GRADED';
  const stageDone        = (hasNoContent || contentAllDone) && (!canvas || canvasPassed);

  return (
    <div className={`border rounded-2xl overflow-hidden ${stageDone ? 'border-forest-200' : 'border-cream-200'}`}>
      {/* Stage header */}
      <div className={`flex items-center gap-3 px-4 py-3 ${stageDone ? 'bg-forest-50' : 'bg-white'}`}>
        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold border-2 ${
          stageDone
            ? 'bg-forest-600 border-forest-600 text-white'
            : 'border-cream-300 text-forest-500'
        }`}>
          {stageDone ? '✓' : stage.order}
        </div>
        <div className="flex-1 min-w-0">
          <span className={`font-sans font-semibold text-sm ${stageDone ? 'text-forest-600' : 'text-forest-900'}`}>
            {stage.name}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 text-xs text-forest-400">
          {totalContent > 0 && (
            <span className="flex items-center gap-1">
              <BookOpen size={11} /> {completedContent}/{totalContent}
            </span>
          )}
          {canvas && <CanvasBadge submission={submission} />}
        </div>
      </div>

      {/* Content items */}
      {totalContent > 0 && (
        <div className="divide-y divide-cream-100 bg-white border-t border-cream-100">
          {stage.contents.map(content => (
            <ContentRow
              key={content.id}
              content={content}
              onComplete={onComplete}
              locked={isLevelLocked}
            />
          ))}
        </div>
      )}

      {hasNoContent && !canvas && (
        <div className="px-4 py-3 bg-white border-t border-cream-100 text-xs text-forest-400 italic">
          No content added to this stage yet
        </div>
      )}

      {/* Canvas activity */}
      {canvas && (
        <div className={`border-t px-4 py-3 flex items-center justify-between gap-3 ${
          canvasUnlocked ? 'bg-white border-cream-100' : 'bg-cream-50 border-cream-100 opacity-60'
        }`}>
          <div className="flex items-center gap-2.5 min-w-0">
            <PenLine size={14} className={canvasUnlocked ? 'text-forest-500' : 'text-cream-400'} />
            <div className="min-w-0">
              <div className={`text-sm font-medium truncate ${canvasUnlocked ? 'text-forest-800' : 'text-forest-400'}`}>
                {canvas.title}
              </div>
              <div className="text-xs text-forest-400 mt-0.5">
                {canvasPassed
                  ? `Score: ${submission.score}/${canvas.maxScore}`
                  : submission?.status === 'SUBMITTED'
                    ? 'Under review · tap to view your submission'
                    : submission?.status === 'REVISION_REQUESTED'
                      ? 'Revision requested — resubmit'
                      : canvasUnlocked
                        ? 'Submit your work for this stage'
                        : totalContent > 0
                          ? `Complete all ${totalContent} content items first`
                          : 'Loading...'}
              </div>
            </div>
          </div>
          <div className="flex-shrink-0">
            {canvasPassed ? (
              <CheckCircle2 size={18} className="text-forest-500" />
            ) : !canvasUnlocked ? (
              <Lock size={15} className="text-cream-400" />
            ) : submission?.status === 'SUBMITTED' ? (
              <Link
                to={`/canvas/${stage.id}`}
                className="flex items-center gap-1.5 text-xs text-amber-600 font-medium border border-amber-200 bg-amber-50 hover:bg-amber-100 px-3 py-1.5 rounded-full transition-colors"
              >
                <Eye size={12} /> View Submission
              </Link>
            ) : (
              <Link
                to={`/canvas/${stage.id}`}
                className="btn-primary text-xs py-1.5 px-3"
              >
                {submission?.status === 'REVISION_REQUESTED' ? 'Resubmit →' : 'Submit →'}
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ── Level detail — lazy loaded when expanded ──────────────────
const LevelDetail = ({ levelId, isLocked, sessionDone, onComplete }) => {
  const { data, loading, error } = useApi(
    () => progressService.getLevelProgress(levelId),
    [levelId],
  );

  const stages = data?.stages ?? [];
  const allStagesDone = stages.length > 0 && stages.every(s => {
    const totalContent     = s.contents?.length ?? 0;
    const completedContent = s.contents?.filter(c => c.progress?.status === 'COMPLETED').length ?? 0;
    const contentAllDone   = totalContent === 0 || completedContent === totalContent;
    const canvasPassed     = !s.canvasActivity || s.canvasActivity?.submission?.status === 'GRADED';
    return contentAllDone && canvasPassed;
  });

  if (loading) return (
    <div className="space-y-2 pt-3">
      {[1,2,3].map(i => <div key={i} className="h-14 bg-cream-100 rounded-xl animate-pulse" />)}
    </div>
  );

  if (error) return (
    <div className="pt-3 text-xs text-rust-500 flex items-center gap-2">
      <AlertCircle size={13} /> Could not load stage details
    </div>
  );

  if (stages.length === 0) return (
    <div className="pt-3 text-xs text-forest-400 italic">No stages configured for this level yet.</div>
  );

  return (
    <div className="pt-4 space-y-3">
      {/* Stages section */}
      <p className="text-xs font-semibold text-forest-500 uppercase tracking-widest">
        {/* Stages & Canvas Activities */}
        Tourent Cycle Activities
      </p>
      {stages.map(stage => (
        <StageBlock
          key={stage.id}
          stage={stage}
          isLevelLocked={isLocked}
          onComplete={onComplete}
        />
      ))}

      {/* Session — shown AFTER stages, as the final step */}
      {stages.length > 0 && (
        <div className={`border rounded-2xl p-4 flex items-center justify-between gap-3 ${
          sessionDone
            ? 'bg-forest-50 border-forest-200'
            : allStagesDone
              ? 'bg-blue-50 border-blue-200'
              : 'bg-cream-50 border-cream-100 opacity-60'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
              sessionDone ? 'bg-forest-600' : allStagesDone ? 'bg-blue-500' : 'bg-cream-200'
            }`}>
              <Video size={16} className={sessionDone || allStagesDone ? 'text-white' : 'text-cream-400'} />
            </div>
            <div>
              <div className={`text-sm font-semibold ${sessionDone ? 'text-forest-700' : allStagesDone ? 'text-blue-800' : 'text-forest-400'}`}>
                {sessionDone ? 'Live Session Completed ✓' : 'Book Live Session with Expert'}
              </div>
              <div className="text-xs text-forest-400 mt-0.5">
                {sessionDone
                  ? 'You attended your expert mentorship session'
                  : allStagesDone
                    ? 'All stages done! Now book your expert session to complete this level.'
                    : 'Complete all stages above first, then book your session'}
              </div>
            </div>
          </div>
          {!sessionDone && allStagesDone && (
            <Link to="/experts" className="btn-primary text-xs py-2 px-4 flex-shrink-0 flex items-center gap-1.5">
              <Calendar size={13} /> Book Session
            </Link>
          )}
          {sessionDone && <CheckCircle2 size={18} className="text-forest-500 flex-shrink-0" />}
        </div>
      )}
    </div>
  );
};

// ── Level Card ────────────────────────────────────────────────
const LevelCard = ({ level, isCurrent, isLocked }) => {
  const [expanded,   setExpanded]   = useState(isCurrent);
  const [refetchKey, setRefetchKey] = useState(0);

  const progress       = level.progress ?? {};
  const totalContent   = progress.totalContent   ?? level.totalContent   ?? 0;
  const completedContent = progress.completedContent ?? level.completedContent ?? 0;
  const contentDone    = !!progress.contentCompleted || (totalContent > 0 && completedContent >= totalContent);
  const sessionDone    = !!progress.sessionCompleted;
  const canvasDone     = !!progress.canvasCompleted;
  const doneCount      = [contentDone, sessionDone, canvasDone].filter(Boolean).length;
  const pct            = level.isCompleted ? 100 : Math.round((doneCount / 3) * 100);

  const handleAdvance = async () => {
    try {
      await progressService.advance();
      toast.success('Advanced to next level!');
      window.location.reload();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not advance');
    }
  };

  return (
    <div className={`bg-white border-2 rounded-2xl overflow-hidden transition-all ${
      isCurrent         ? 'border-forest-600 shadow-md'
      : level.isCompleted ? 'border-green-200'
      : isLocked          ? 'border-cream-100'
      :                     'border-cream-200'
    }`}>
      {/* Header */}
      <button onClick={() => setExpanded(!expanded)} className="w-full text-left p-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              {isCurrent && (
                <span className="text-[10px] font-semibold bg-forest-800 text-cream-50 px-2 py-0.5 rounded-full">
                  Current Level
                </span>
              )}
              {level.isCompleted && (
                <span className="text-[10px] font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                  ✓ Completed
                </span>
              )}
              {isLocked && (
                <span className="text-[10px] font-medium bg-cream-100 text-forest-400 px-2 py-0.5 rounded-full flex items-center gap-1">
                  <Lock size={8} /> Locked
                </span>
              )}
            </div>
            <h3 className={`font-serif text-lg font-semibold ${isLocked ? 'text-forest-300' : 'text-forest-900'}`}>
              {level.name}
            </h3>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={`font-serif text-2xl font-bold ${
              level.isCompleted ? 'text-green-600' : isCurrent ? 'text-forest-700' : 'text-forest-300'
            }`}>{pct}%</span>
            {expanded
              ? <ChevronUp size={16} className="text-forest-400" />
              : <ChevronDown size={16} className="text-forest-400" />}
          </div>
        </div>
        <div className="h-1.5 bg-cream-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-700 ${level.isCompleted ? 'bg-green-500' : 'bg-forest-600'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-5 pb-5 border-t border-cream-100">
          {!isLocked ? (
            <LevelDetail
              key={refetchKey}
              levelId={level.id}
              isLocked={isLocked}
              sessionDone={sessionDone}
              onComplete={() => setRefetchKey(k => k + 1)}
            />
          ) : (
            <div className="pt-4 space-y-1.5">
              <div className="text-sm text-forest-400 flex items-center gap-2">
                <Lock size={14} /> Complete the previous level to unlock this one.
              </div>
              <p className="text-xs text-forest-300 ml-6">
                Finish all content, attend a live session and pass your canvas activity — the next level unlocks automatically.
              </p>
            </div>
          )}

          {/* Advance button */}
          {isCurrent && level.isCompleted && (
            <button onClick={handleAdvance} className="btn-primary w-full justify-center mt-4 text-sm py-2.5">
              Advance to Next Level →
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────
const ProgressPage = () => {
  const { data, loading, error } = useApi(() => progressService.getAllLevels());
  const levels = Array.isArray(data) ? data
    : Array.isArray(data?.levels) ? data.levels
    : Array.isArray(data?.data)   ? data.data : [];
  const completed = levels.filter(l => l.isCompleted).length;

  return (
    <DashboardLayout title="Tourent Cycle Progress">
      <div className="max-w-3xl space-y-6">

        {/* Summary */}
        {!loading && levels.length > 0 && (
          <div className="bg-forest-900 rounded-2xl p-5 flex items-center gap-5">
            <div className="w-12 h-12 bg-forest-700 rounded-2xl flex items-center justify-center flex-shrink-0">
              <TrendingUp size={22} className="text-forest-300" />
            </div>
            <div className="flex-1">
              <div className="font-serif text-2xl text-cream-50 font-bold">
                {completed} / {levels.length} levels completed
              </div>
              <div className="text-sm text-forest-400 mt-0.5">Keep going — you're on track!</div>
            </div>
            <div className="hidden sm:block w-20 h-20 relative flex-shrink-0">
              <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="#1a5e1a" strokeWidth="3" />
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="#4ea34e" strokeWidth="3"
                  strokeDasharray={`${levels.length ? (completed / levels.length) * 100 : 0}, 100`}
                  strokeLinecap="round" />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center font-serif text-sm font-bold text-cream-50">
                {levels.length ? Math.round((completed / levels.length) * 100) : 0}%
              </div>
            </div>
          </div>
        )}

        {/* How it works callout */}
        {!loading && levels.length > 0 && (
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 text-sm text-blue-800 flex items-start gap-3">
            <div className="flex-shrink-0 text-lg">💡</div>
            <div>
              <strong>How to complete a level:</strong> For each stage, watch/read the content → submit your canvas assignment → then book a live session with an expert to finalise the level.
            </div>
          </div>
        )}

        {error && (
          <div className="flex items-center gap-3 text-rust-500 bg-rust-50 border border-rust-200 rounded-xl px-5 py-4">
            <AlertCircle size={16} />
            <span className="text-sm">Could not load progress — API may be unreachable.</span>
          </div>
        )}

        {loading ? (
          <div className="space-y-4">
            {[1,2,3].map(i => (
              <div key={i} className="bg-white border border-cream-200 rounded-2xl p-5 animate-pulse space-y-3">
                <div className="h-5 bg-cream-200 rounded w-1/2" />
                <div className="h-2 bg-cream-100 rounded-full" />
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-4">
            {levels.map(level => (
              <LevelCard
                key={level.id}
                level={level}
                isCurrent={level.isCurrent}
                isLocked={level.isLocked}
              />
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ProgressPage;